/**
 * Seleção de itens de uma categoria, ligada ao react-hook-form.
 *
 * Duas memórias, de propósito:
 *
 *  - Os IDENTIFICADORES vivem no form state, como `string[]` ordenado. É o que
 *    vai para o servidor, e é o que sobrevive a buscar, paginar e trocar de aba,
 *    porque o form state é da página e não do painel.
 *
 *  - Os RÓTULOS vivem num Map próprio, que nunca é enviado. Existe porque um item
 *    marcado na página 1 precisa continuar nomeado quando o usuário está na página
 *    7 ou filtrando por outro termo. Sem ele, "12 selecionados" seria um número
 *    sem nomes.
 *
 * A lista é gravada ORDENADA, e isso não é estética: o `isDirty` do react-hook-form
 * compara posição a posição, então uma lista reordenada marcaria como sujo um
 * formulário que ninguém alterou, e a guarda de saída dispararia à toa.
 */
import { useCallback, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

/** `all` libera a categoria inteira; `subset` restringe aos itens marcados. */
export type SelectionMode = 'all' | 'subset'

/** O mínimo para desenhar um item selecionado que não está na página vigente. */
export interface SelectedLabel {
  uuid: string
  label: string
  sublabel?: string
}

export interface SelectionController {
  mode: SelectionMode
  setMode: (mode: SelectionMode) => void
  selected: ReadonlySet<string>
  count: number
  labels: ReadonlyMap<string, SelectedLabel>
  isSelected: (uuid: string) => boolean
  toggle: (item: SelectedLabel) => void
  /** Marca ou desmarca um LOTE. É o que a caixa do cabeçalho usa. */
  setMany: (items: SelectedLabel[], on: boolean) => void
  clear: () => void
  /** Alimenta o cache de rótulos com o que o servidor devolveu na edição. */
  seed: (items: SelectedLabel[]) => void
}

const VAZIO: string[] = []

/** Estado inicial de uma categoria: acesso a todos, sem item marcado. */
export const ESCOPO_LIBERADO = { mode: 'all' as SelectionMode, uuids: VAZIO }

/** `base` é o caminho no formulário, por exemplo `scopes.customers`. */
export function useSelectionField(base: string): SelectionController {
  const { setValue } = useFormContext()
  const mode = (useWatch({ name: `${base}.mode` }) as SelectionMode | undefined) ?? 'all'
  const uuids = (useWatch({ name: `${base}.uuids` }) as string[] | undefined) ?? VAZIO
  const [labels, setLabels] = useState<Map<string, SelectedLabel>>(() => new Map())

  const selected = useMemo(() => new Set(uuids), [uuids])

  const lembrar = useCallback((itens: SelectedLabel[]) => {
    if (!itens.length) return
    setLabels((anterior) => {
      let mudou = false
      const proximo = new Map(anterior)
      for (const item of itens) {
        const atual = proximo.get(item.uuid)
        if (!atual || atual.label !== item.label || atual.sublabel !== item.sublabel) {
          proximo.set(item.uuid, item)
          mudou = true
        }
      }
      return mudou ? proximo : anterior
    })
  }, [])

  const gravar = useCallback(
    (proximo: Set<string>) =>
      setValue(`${base}.uuids`, [...proximo].sort(), { shouldDirty: true }),
    [base, setValue],
  )

  return useMemo<SelectionController>(
    () => ({
      mode,
      setMode: (m) => setValue(`${base}.mode`, m, { shouldDirty: true }),
      selected,
      count: uuids.length,
      labels,
      isSelected: (uuid) => selected.has(uuid),
      toggle: (item) => {
        const proximo = new Set(selected)
        if (proximo.has(item.uuid)) {
          proximo.delete(item.uuid)
        } else {
          proximo.add(item.uuid)
          lembrar([item])
        }
        gravar(proximo)
      },
      setMany: (itens, on) => {
        const proximo = new Set(selected)
        for (const item of itens) {
          if (on) proximo.add(item.uuid)
          else proximo.delete(item.uuid)
        }
        if (on) lembrar(itens)
        gravar(proximo)
      },
      clear: () => gravar(new Set()),
      seed: lembrar,
    }),
    [base, mode, selected, uuids.length, labels, setValue, gravar, lembrar],
  )
}
